import {
    Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Badge, Box, Button,
    Checkbox, Divider, HStack, Icon,
    Link, ListItem, Skeleton, Text, UnorderedList, useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { IoCheckmarkCircleSharp } from "react-icons/io5";
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import { useDebounce } from 'use-debounce';
import useFetch, { CachePolicies } from 'use-http';
import GettingStartedGraphic from '../../../graphics/GettingStartedGraphic';
import { ucFirst } from '../../../utils';
import { ChecklistItem, UserSessionResponse } from '../../interfaces';
import { codeBlockStyles, ReactSyntaxHighlighterWrapper } from "../../layouts/Developer/ApiKeysLayout";


const notUnderUserControl = ['sourcesAdded', 'imageUploaded'];


const LoadingSkeleton = (): React.ReactElement => {
    return (
        <Skeleton borderRadius={"8px"} height={"300px"} width={"full"} />
    );
}

export const Checklist = (): React.ReactElement => {
    const { productAdoptionMetrics, user, loading, dispatchUpdateMetrics } = useProductOnboardingMetrics();
    const [debouncedProductMetrics] = useDebounce(productAdoptionMetrics, 300);
    const toast = useToast();
    const { request } = useFetch({
        cachePolicy: CachePolicies.NO_CACHE,
    })

    useEffect(() => {
        const postMetrics = async () => {
            if (!user) return;
            if (user.canUpdateUCMetricsPermission === false) return;
            await request.post(
                `/api/clients/${user.clientNumber}/onboarding/uc-metrics`,
                debouncedProductMetrics,
            )

            if (request.error) {
                toast({
                    title: "Error",
                    description: "Failed in saving. Please refresh the page and try again.",
                    status: "error",
                    duration: 9000,
                    isClosable: true,
                });
            }
        };

        postMetrics();
    }, [debouncedProductMetrics]);

    if (loading) return <LoadingSkeleton />;

    const checklistItems = getCheckListItems({ user });

    const setMetricValue = ({ metricName, checked }) => {
        if (!notUnderUserControl.includes(metricName)) {
            dispatchUpdateMetrics(prevMetrics => {
                const updatedMetrics = {
                    ...prevMetrics,
                    [metricName]: checked
                };

                return updatedMetrics;
            });
        }
    };

    return (
        <Accordion allowToggle={true} p={0} w={"full"} defaultIndex={0}>
            <AccordionItem w="full" p={0} border={"none"} >
                {({ isExpanded }) => (
                    <Box bg={"appPaleGrey"} borderRadius={"8px"} padding={8} w={"full"} pb={isExpanded ? 0 : 8}>
                        <AccordionButton p={0} outline="none" _focus={{ boxShadow: "none" }} _hover={{ backgroundColor: "default" }}>
                            <VStack spacing={4} alignItems={"start"} w={"full"}>
                                <HStack
                                    spacing={4}
                                    alignItems={"flex-start"}
                                    w={"full"}
                                >
                                    <GettingStartedGraphic width="80px" height="80px" />
                                    <VStack alignItems={"start"} spacing={2}>
                                        <Heading user={user} checklistItems={checklistItems} productAdoptionMetrics={productAdoptionMetrics} />
                                        <SubHeading user={user} checklistItems={checklistItems} productAdoptionMetrics={productAdoptionMetrics} />
                                        <HStack w={"full"} alignItems={"center"} spacing={4}>
                                            <Status checklistItems={checklistItems} productAdoptionMetrics={productAdoptionMetrics} />
                                            <Link>
                                                <HStack>
                                                    <Text>{isExpanded ? "Hide checklist" : "Show checklist"}</Text>
                                                    <AccordionIcon />
                                                </HStack>
                                            </Link>
                                        </HStack>
                                    </VStack>
                                </HStack>
                            </VStack>
                        </AccordionButton>
                        <AccordionPanel p={0} b={0}>
                            <Box w="full" h={"60px"} display="flex" alignItems={"center"} justifyContent={"center"}>
                                <Divider />
                            </Box>

                            <VStack alignItems={"start"} spacing={4}>
                                <CheckListHeading user={user} />
                                <Box w={"full"}>
                                    <Accordion allowToggle={true}>
                                        {checklistItems.map((item, index) => (
                                            <AccordionItem
                                                key={index}
                                                w="full"
                                                borderTop={index === 0 ? "none" : "default"}
                                                borderBottom={index === checklistItems.length - 1 ? "none" : "default"}
                                            >
                                                <h2>
                                                    <AccordionButton p={"32px 0"} outline="none" _focus={{ boxShadow: "none" }} _hover={{ backgroundColor: "default" }} _expanded={{ paddingBottom: 5 }}>
                                                        <HStack alignItems="center" w="full">
                                                            <Icon as={IoCheckmarkCircleSharp} boxSize={"25px"} color={productAdoptionMetrics[item.metricName] ? "green.500" : "gray.300"} />
                                                            <Box flex="1" textAlign="left">
                                                                <Text fontSize={"14px"} fontWeight={"semibold"}>{item.title}</Text>
                                                            </Box>
                                                            <AccordionIcon />
                                                        </HStack>
                                                    </AccordionButton>
                                                </h2>
                                                <AccordionPanel pt={0}>
                                                    <VStack align="left" spacing={5} pl={5}>
                                                        {item.description &&
                                                            <Box>
                                                                {item.description}
                                                            </Box>
                                                        }
                                                        {item.CTAs?.length &&
                                                            <HStack alignItems={"center"} spacing={4}>
                                                                {item.CTAs?.map((cta, index) => (
                                                                    <Button target={cta.link.startsWith("http") ? "_blank" : ""} as="a" key={index} href={cta.link} size="sm" variant={"outline"}>
                                                                        {cta.text}
                                                                    </Button>
                                                                ))}
                                                            </HStack>
                                                        }
                                                        {!notUnderUserControl.includes(item.metricName) &&
                                                            <Box>
                                                                <Checkbox
                                                                    onChange={(e) => setMetricValue({
                                                                        metricName: item.metricName,
                                                                        checked: e.target.checked
                                                                    })}
                                                                    isDisabled={user?.canUpdateUCMetricsPermission === true ? false : true}
                                                                    defaultChecked={productAdoptionMetrics[item.metricName]}
                                                                    color={"appSlate"}>{productAdoptionMetrics[item.metricName] ? "Mark as incomplete" : "Mark as complete"}</Checkbox>
                                                            </Box>
                                                        }
                                                    </VStack>
                                                </AccordionPanel>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </Box>
                            </VStack >
                        </AccordionPanel>
                    </Box>
                )}
            </AccordionItem>
        </Accordion>
    );
}

const Status = ({ checklistItems, productAdoptionMetrics }) => {
    const completedItemsCount = checklistItems.filter(item => {
        return productAdoptionMetrics[item.metricName]
    }).length;
    const totalItemsCount = checklistItems.length;
    const isCompleted = completedItemsCount === totalItemsCount;

    const bgColor = isCompleted ? "#e6faf0" : "#e2e8f0";
    const textColor = isCompleted ? "#009b52" : "#4a5568";
    const text = isCompleted ? "Onboarding complete" : `${completedItemsCount}/${totalItemsCount} completed`;

    return (
        <Badge bg={bgColor} borderRadius={"4px"} p={"4px"}>
            <HStack>
                <Text color={textColor} fontSize={"14px"} fontWeight={"normal"} textTransform={"none"}>
                    {text}
                </Text>
            </HStack>
        </Badge>
    )
}

const Heading = ({ user, checklistItems, productAdoptionMetrics }) => {
    const allItemsComplete = checklistItems.every(item => {
        return productAdoptionMetrics[item.metricName]
    });
    // const completed
    if (!allItemsComplete) {
        return (
            <Text fontSize={"20px"} fontWeight={"semibold"}>
                Hey {ucFirst(user?.userName)}, welcome to ImageKit!
            </Text>
        )
    } else {
        return (
            <Text fontSize={"20px"} fontWeight={"semibold"}>
                Congrats, {ucFirst(user?.userName)}! Onboarding complete.
            </Text>
        )
    }

}

const CheckListHeading = ({ user }) => {
    if (user?.pricingPlan?.planType === "DAM_ONLY") {
        return (
            <Text as="h2" fontSize={"18px"} fontWeight={"semibold"}>
                Get started with Digital Asset Management
            </Text>
        )
    } else {
        return (
            <Text as="h2" fontSize={"18px"} fontWeight={"semibold"}>
                Get started with Media Delivery
            </Text>
        )
    }
}

const SubHeading = ({ user, checklistItems, productAdoptionMetrics }) => {
    const allItemsComplete = checklistItems.every(item => {
        return productAdoptionMetrics[item.metricName]
    });
    if (allItemsComplete) {
        return (
            <Text color={"appSlate"} fontSize={"14px"} textAlign={"left"}>
                Explore additional resources for ImageKit mastery and stay tuned for exciting updates!
            </Text>
        )
    } else if (user?.pricingPlan?.planType === "DAM_ONLY") {
        return (
            <Text color={"appSlate"} fontSize={"14px"} textAlign={"left"}>
                Complete your onboarding to manage and collaborate on your digital assets seamlessly.
            </Text>
        )
    } else {
        return (
            <Text color={"appSlate"} fontSize={"14px"} textAlign={"left"}>
                Complete the onboarding to start delivering optimized and transformed images and videos.
            </Text>
        )
    }
}

export const getCheckListItems = ({ user }): ChecklistItem[] => {
    const DAM_ONLY_CHECKLIST: ChecklistItem[] = [
        {
            title: "Upload or migrate your digital assets",
            metricName: "imageUploaded",
            description: <Text color={"appSlate"}>
                Depending on your needs, you can upload media assets to ImageKit from the dashboard, programmatically using API, or through bulk migration (contact us for assistance) with Rclone.
            </Text>,
            CTAs: [
                {
                    text: "Upload media directly",
                    link: "/dashboard/media-library"
                },
                {
                    text: "Upload media via API",
                    link: "https://imagekit.io/docs/api-reference/upload-file/upload-file#Upload-file-V1"
                },
                {
                    text: "Migrate using Rclone",
                    link: "https://imagekit.io/docs/dam/bulk-upload-using-rclone"
                }
            ]

        },
        {
            title: "Organize and manage your digital assets",
            description: <Text color={"appSlate"}>
                Leverage features like <Link href="https://imagekit.io/docs/dam/tagging-media-assets" target="_blank">tagging</Link>, <Link href="https://imagekit.io/docs/dam/custom-metadata" target="_blank">custom metadata</Link> to arrange assets in <Link href="https://imagekit.io/docs/dam/upload-assets#create-new-folder" target="_blank">folders</Link> and shareable <Link href="https://imagekit.io/docs/dam/media-collection" target="_blank">Media Collections</Link>.
            </Text>,
            metricName: "ucOrganizeAndManageAssets",
            CTAs: [
                {
                    text: "Add Tags",
                    link: "https://imagekit.io/docs/dam/tagging-media-assets#ai-powered-auto-tagging"
                },
                {
                    text: "Create Custom Metadata fields",
                    link: "/dashboard/settings/media-library"
                },
                {
                    text: "Create Media Collection",
                    link: "/dashboard/media-collections"
                }
            ]
        },
        {
            title: "Share assets with team members",
            description: <Text color={"appSlate"}>
                ImageKit DAM supports both internal and external stakeholders. Invite them as a user to share assets with them, or use public links to share externally. <Link href="https://imagekit.io/docs/dam/overview#dam-users" target="_blank">Learn more</Link> about DAM users and access control.
            </Text>,
            CTAs: [
                {
                    text: "Invite a team member",
                    link: "/dashboard/user-management/users"
                },
                {
                    text: "Share assets via Public Link",
                    link: "https://imagekit.io/docs/dam/public-links"
                }
            ],
            metricName: "ucShareAssetsWithTeam"
        },
        {
            title: "Collaborate with your team",
            description: <Text color={"appSlate"}>
                Get more work done with your team using features like <Link href="https://imagekit.io/docs/dam/comments-on-assets" target="_blank">Comments</Link>, <Link href="https://imagekit.io/docs/dam/asset-versioning" target="_blank"> Versioning</Link>.
            </Text>,
            metricName: "ucCollaborateWithTeam"
        },
        {
            title: "Download ImageKit DAM App",
            description: <Text color={"appSlate"}>
                Streamline your creative workflows with a dedicated desktop app for MacOS and Windows.
            </Text>,
            CTAs: [
                {
                    text: "Download Desktop App",
                    link: "https://imagekit.io/docs/dam/app#installation"
                }
            ],
            metricName: "ucDownloadDAMApp"
        },
    ];

    const NORMAL_CHECKLIST: ChecklistItem[] = [
        {
            title: "Upload files or connect external storage",
            description: <Text color={"appSlate"}>
                Select how you want to integrate your media with ImageKit: upload through our user-friendly DAM interface, use our upload APIs, or connect ImageKit to your existing external storage.
            </Text>,
            CTAs: [
                {
                    text: "Upload media directly",
                    link: "/dashboard/media-library"
                },
                {
                    text: "Upload media via API",
                    link: "https://imagekit.io/docs/api-reference/upload-file/upload-file#Upload-file-V1"
                },
                {
                    text: "Connect external storage",
                    link: "/dashboard/external-storage"
                }
            ],
            metricName: "sourcesAdded"
        },
        {
            title: "Set up and verify your URL endpoint",
            description:
                <VStack color={"appSlate"} spacing={4}>
                    <Box>
                        <Text as="span" fontWeight={"semibold"}>Set up URL Endpoint: </Text>
                        <Text as="span">Your first external storage is accessible through the provided default URL endpoint. To create additional endpoints, go to the <Link href="/dashboard/url-endpoints">URL Endpoint section</Link> on your ImageKit dashboard.</Text>
                    </Box>
                    <Box>
                        <Text as="span" fontWeight={"semibold"}>Verify URL Endpoint: </Text>
                        <Text as="span">Ensure your media files are optimized and accessible through the URL endpoint. For issues, consult your <Link href="https://imagekit.io/docs/integration/connect-external-storage" target="_blank">external storage integration guide</Link> or our <Link href="https://imagekit.io/docs/integration/connect-external-storage#troubleshooting-404-errors" target="_blank">error debugging guide</Link>.</Text>
                    </Box>
                </VStack>,
            metricName: "ucSetupAndVerifyURLEndpoint"
        },
        {
            title: "Integrate to deliver optimize images and videos",
            description: <Text color={"appSlate"}>
                Integrate ImageKit quickly using our platform-specific guides for React, Angular, Android, Java, and more. The guides include comprehensive media delivery and upload solutions.
            </Text>,
            CTAs: [
                { text: "View quick-start guides", link: "https://imagekit.io/docs/quick-start-guides" },
            ],
            metricName: "ucIntegrateToOptimizeDelivery"
        },
        {
            title: "Transform and stream images and videos",
            description:
                <VStack color={"appSlate"} align={"left"} spacing={4}>
                    <Box>
                        <Text>ImageKit offers <Link href="https://imagekit.io/docs/transformations" target="_blank">50+ URL-based transformations</Link> for dynamic media optimization. Here are some examples to get started:</Text>
                    </Box>
                    <UnorderedList pl={8}>
                        <ListItem>
                            Resize images and videos to fit your layout. Leverage AI-powered smart and object-based cropping to get the generate the perfect thumbnail.
                        </ListItem>
                        <ListItem>
                            Create video posters, resize and apply effects to videos.
                        </ListItem>
                        <ListItem>
                            Add overlays on images and videos, apply filters, and more.
                        </ListItem>
                    </UnorderedList>
                    <Text>
                        We maintain SDKs for various front-end, back-end, and mobile platforms and have listed plugins in marketplaces like WordPress, Magento, etc. Checkout <Link href="https://imagekit.io/docs/quick-start-guides" target="_blank">quick start guides</Link>.
                    </Text>
                    <Box maxW={"625px"} overflow={"hidden"}>
                        <ReactSyntaxHighlighterWrapper
                            language={"js"}
                            style={vs2015}
                            wrapLines={true}
                            customStyle={codeBlockStyles}
                            codeTagProps={{
                                style: { fontFamily: 'monospace,monospace !important' },
                            }}
                        >
                            {
                                `// Basic example and URL structure

        URL endpoint        transformation    asset path 
┌──────────────────────────┐┌────────────┐┌────────────────┐
https://ik.imagekit.io/demo/tr:w-300,h-300/example-asset.jpg


// Original image URL
https://ik.imagekit.io/ikmedia/docs_images/examples/example_food_3.jpg


// To resize this image to 300x300 pixels, you can append the tr:w-300,h-300 transformation parameter to the asset URL
https://ik.imagekit.io/demo/img/tr:w-200,h-200/truck.jpg


                               transformation
                               ┌────────────┐
https://ik.imagekit.io/ikmedia/tr:w-300,h-300/docs_images/examples/example_food_3.jpg

//The same transformation can be passed as query parameters as well

                                                                       transformation
                                                                       ┌────────────┐
https://ik.imagekit.io/ikmedia/docs_images/examples/example_food_3.jpg?tr=w-300,h-300`
                            }

                        </ReactSyntaxHighlighterWrapper>
                    </Box>

                </VStack>,
            CTAs: [
                {
                    text: "Explore transformations",
                    link: "https://imagekit.io/docs/transformations"
                }
            ],
            metricName: "ucTransformAndStreamMedia"
        },
        {
            title: "Validate performance, cross-check production checklist and go-live",
            description:
                <VStack color={"appSlate"} align={"left"} spacing={4}>
                    <Box>
                        <Text as="span" fontWeight={"semibold"}>Validate Website Performance: </Text>
                        <Text as="span">Check media related performance issues using <Link href="https://imagekit.io/website-analyzer/" target="_blank">website analyzer</Link>.</Text>
                    </Box>
                    <Box>
                        <Text as="span" fontWeight={"semibold"}>Cross-Check Production Checklist: </Text>
                        <Text as="span">Review the checklist before going live to ensure you're prepared and maximizing ImageKit’s benefits.</Text>
                    </Box>
                    <UnorderedList pl={8}>
                        <ListItem>
                            <Text as="span" fontWeight={"semibold"}>Review Optimization Settings: </Text>Adjust default optimization settings for <Link href="https://imagekit.io/docs/optimize-image-and-video-for-web-delivery" target="_blank">desktop</Link> and <Link href="https://imagekit.io/docs/production-checklist#mobile-app-specific-optimizations" target="_blank">mobile apps</Link>.
                        </ListItem>
                        <ListItem>
                            <Text as="span" fontWeight={"semibold"}>Enable 2FA: </Text>Improve account security by activating 2FA in the <Link href="https://imagekit.io/dashboard/user-management/users" target="_blank">User Management section</Link>.
                        </ListItem>
                        <ListItem>
                            <Text as="span" fontWeight={"semibold"}>Secure Media Delivery: </Text>Use <Link href="https://imagekit.io/docs/transformations#named-transformations" target="_blank">Named Transformations</Link> and <Link href="https://imagekit.io/docs/media-delivery-basic-security#how-to-generate-signed-urls" target={"_blank"}>Signed URLs</Link>.
                        </ListItem>
                    </UnorderedList>
                </VStack>,
            CTAs: [
                {
                    text: "View complete checklist",
                    link: "https://imagekit.io/docs/production-checklist"
                }
            ],
            metricName: "ucValidatePerformanceAndGoLive"
        },
    ];

    const checklistItems = user?.pricingPlan?.planType === "DAM_ONLY" ? DAM_ONLY_CHECKLIST : NORMAL_CHECKLIST;

    return checklistItems
}

export const useProductOnboardingMetrics = () => {
    const [productAdoptionMetrics, setProductAdoptionMetrics] = useState({});
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<UserSessionResponse | null>(null);

    useEffect(() => {
        // Event handler for the custom event
        const handleUpdateMetrics = (event) => {
            setProductAdoptionMetrics(event.detail);
        };

        const fetchLatestMetrics = async () => {
            setLoading(true);
            // GET /api/session
            const response = await fetch('/api/session');
            const data = await response.json() as UserSessionResponse;
            setUser(data);
            setProductAdoptionMetrics(data?.onboardingMetrics);
            setLoading(false);
        };

        // Listen for the custom event
        window.addEventListener('updateProductMetrics', handleUpdateMetrics);

        fetchLatestMetrics();
        // Cleanup the event listener on unmount
        return () => {
            window.removeEventListener('updateProductMetrics', handleUpdateMetrics);
        };
    }, []);

    // Optional: Provide a function to dispatch the custom event
    const dispatchUpdateMetrics = (metrics) => {
        window.dispatchEvent(new CustomEvent('updateProductMetrics', { detail: metrics }));
    };

    return { productAdoptionMetrics, user, loading, dispatchUpdateMetrics };
};